/**
 * Lista de tareas animada (diseño propio de HabitOS).
 * Al completar, el círculo se rellena, una línea tacha el título y la
 * tarea se desplaza al final con una transición FLIP. Respeta
 * prefers-reduced-motion mediante las reglas globales de styles.css.
 */
import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Check } from 'lucide-react';

export interface TaskItem {
  id: string;
  title: string;
  done: boolean;
  meta?: string;
  overdue?: boolean;
  href: string;
}

export function TaskList({
  items,
  onToggle,
  labels,
}: {
  items: TaskItem[];
  onToggle: (id: string, done: boolean) => void;
  labels: { complete: string; uncomplete: string };
}) {
  const sorted = [...items].sort((a, b) => Number(a.done) - Number(b.done));
  const listRef = useRef<HTMLUListElement>(null);
  const positions = useRef(new Map<string, number>());

  // FLIP: anima el cambio de posición cuando una tarea se completa o reabre.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    list.querySelectorAll<HTMLElement>('[data-task-id]').forEach((el) => {
      const id = el.dataset.taskId!;
      const top = el.getBoundingClientRect().top;
      const prev = positions.current.get(id);
      positions.current.set(id, top);
      if (reduce || prev === undefined || prev === top) return;
      el.animate([{ transform: `translateY(${prev - top}px)` }, { transform: 'translateY(0)' }], {
        duration: 320,
        easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
      });
    });
  });

  return (
    <ul className="tasks" ref={listRef}>
      {sorted.map((t) => (
        <li key={t.id} className="task" data-task-id={t.id} data-done={t.done ? 'true' : undefined}>
          <button
            type="button"
            className="task__check"
            aria-pressed={t.done}
            aria-label={`${t.done ? labels.uncomplete : labels.complete}: ${t.title}`}
            onClick={() => onToggle(t.id, !t.done)}
          >
            <Check size={16} strokeWidth={3.2} aria-hidden="true" />
          </button>
          <Link to={t.href} className="task__body">
            <span className="task__title">{t.title}</span>
            {t.meta && <span className={`task__meta ${t.overdue ? 'task__meta--overdue' : ''}`}>{t.meta}</span>}
          </Link>
        </li>
      ))}
    </ul>
  );
}
